import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, Search } from 'lucide-react';
import { apiClient } from '@/lib/axios';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { formatDateTime } from '@/utils/formatters';
import type { AuditLog } from '@/types';

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: '', entity: '', dateFrom: '', dateTo: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['audit', 'logs', page, filters],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: { data: AuditLog[]; pagination: { total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } } }>(
        '/audit/logs', { params: { page, limit: 50, ...filters } },
      );
      return data.data;
    },
  });

  const ACTION_OPTIONS = [
    { value: '', label: 'All Actions' },
    { value: 'LOGIN', label: 'Login' },
    { value: 'CREATE_LEAD', label: 'Create Lead' },
    { value: 'UPDATE_LEAD', label: 'Update Lead' },
    { value: 'DELETE_LEAD', label: 'Delete Lead' },
    { value: 'VIEW_LEAD', label: 'View Lead' },
    { value: 'PHI_ACCESS', label: 'PHI Access' },
    { value: 'UPLOAD_DOCUMENT', label: 'Upload Document' },
    { value: 'DOWNLOAD_DOCUMENT', label: 'Download Document' },
    { value: 'INITIATE_CALL', label: 'Initiate Call' },
    { value: 'SEND_WHATSAPP', label: 'Send WhatsApp' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield size={24} className="text-brand-navy" />
            Audit Logs
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">HIPAA compliance audit trail — all actions logged</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <Select
            label="Action"
            options={ACTION_OPTIONS}
            value={filters.action}
            onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
          />
          <Input label="From Date" type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
          <Input label="To Date" type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['Timestamp', 'User', 'Role', 'Action', 'Entity', 'PHI', 'IP Address', 'Status'].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data?.data.map(log => (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap font-mono">
                        {formatDateTime(log.createdAt)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-900 text-xs">{log.userName}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">{log.userRole ?? 'system'}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs font-mono font-medium text-brand-navy">{log.action}</span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600 capitalize">{log.entity}</td>
                      <td className="px-4 py-2.5">
                        {log.phiAccessed && (
                          <span className="text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded-full font-medium">PHI</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-400 font-mono">{log.ipAddress ?? '—'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${log.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                          {log.success ? '✓' : '✗'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {!data?.data.length && (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-slate-400 text-sm">No audit logs found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">Total: {data.pagination.total} entries</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="xs" disabled={!data.pagination.hasPrev} onClick={() => setPage(p => p - 1)}>← Prev</Button>
                  <span className="text-xs text-slate-500 flex items-center px-3">{page} / {data.pagination.totalPages}</span>
                  <Button variant="ghost" size="xs" disabled={!data.pagination.hasNext} onClick={() => setPage(p => p + 1)}>Next →</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
